import type React from 'react';
import { toast } from 'react-toastify';
import { InlineEditName } from './InlineEditName';
import styles from './Specifications.module.css';

interface SchemaCardGridProps {
  schemas: any[];
  activeSpecId: string | undefined;
  postmanEnvStatus: Record<string, boolean>;
  urlCheckStatus?: Record<string, 'idle' | 'checking' | 'available' | 'unavailable'>;
  onSelectSpec: (spec: any) => void;
  onConfigure: (schema: any) => void;
  onDelete: (schemaId: string) => void;
  onUploadClick: () => void;
  onRename: (schemaId: string, newName: string) => Promise<void>;
}

export const SchemaCardGrid: React.FC<SchemaCardGridProps> = ({
  schemas,
  activeSpecId,
  postmanEnvStatus,
  urlCheckStatus = {},
  onSelectSpec,
  onConfigure,
  onDelete,
  onUploadClick,
  onRename,
}) => {
  if (schemas.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3>No Schemas Found</h3>
        <p>Upload your first API schema to get started with the Specs Registry.</p>
        <button className={styles.button} data-variant="primary" onClick={onUploadClick}>
          Upload Schema
        </button>
      </div>
    );
  }

  return (
    <div className={styles.schemaGrid}>
      {schemas.map((schema, index) => (
        <div className={styles.schemaCard} key={schema.id || index}>
          <div className={styles.schemaHeader}>
            <InlineEditName schemaId={schema.id} name={schema.name} onRename={onRename} />
            <div className={styles.schemaBadges}>
              <span className={styles.specCategoryBadge} data-category={schema.specCategory || 'openapi'}>
                {schema.specCategory === 'postman' ? 'Postman' : 'OpenAPI'}
              </span>
              {schema.specCategory === 'postman' && postmanEnvStatus[schema.id] === false && (
                <span className={styles.missingEnvBadge} title="Go to Configure and upload an environment file">
                  No Env
                </span>
              )}
              {urlCheckStatus[schema.id] === 'checking' && <span className={styles.checkingBadge}>Checking</span>}
              {urlCheckStatus[schema.id] === 'unavailable' && (
                <span className={styles.unavailableBadge}>Unavailable</span>
              )}
              <span className={styles.schemaVersion}>v{schema.version}</span>
            </div>
          </div>
          <div className={styles.schemaInfo}>
            <div className={styles.schemaDetail}>
              <span className={styles.schemaLabel}>Type:</span>
              <span className={styles.schemaValue}>{schema.type || 'OpenAPI'}</span>
            </div>
            <div className={styles.schemaDetail}>
              <span className={styles.schemaLabel}>Endpoints:</span>
              <span className={styles.schemaValue}>{schema.endpointCount || 0}</span>
            </div>
            <div className={styles.schemaDetail}>
              <span className={styles.schemaLabel}>Size:</span>
              <span className={styles.schemaValue}>{schema.size || '0 KB'}</span>
            </div>
            <div className={styles.schemaDetail}>
              <span className={styles.schemaLabel}>Modified:</span>
              <span className={styles.schemaValue}>
                {new Date(schema.lastModified || Date.now()).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className={styles.schemaActions}>
            <button
              className={styles.smallButton}
              data-variant={activeSpecId === schema.id ? 'success' : 'primary'}
              onClick={() => {
                onSelectSpec({
                  id: schema.id,
                  name: schema.name,
                  version: schema.version,
                  endpointCount: schema.endpointCount,
                  specCategory: schema.specCategory || 'openapi',
                });
                toast.success(`${schema.name} is now the active specification`);
              }}
              disabled={activeSpecId === schema.id}
            >
              {activeSpecId === schema.id ? 'Active' : 'Set as Active'}
            </button>
            <button className={styles.smallButton} data-variant="primary" onClick={() => onConfigure(schema)}>
              Configure
            </button>
            <button className={styles.smallButton} onClick={() => onDelete(schema.id)}>
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
